/**
 * CRM clients finaux (parité legacy Customers tab) — logique PURE et testable.
 * Segmentation, KPIs et filtrage de la base clients d'un resto. Les données viennent
 * de customer_tenants (× customers) ; aucun calcul caché, la base fait foi.
 */

// Parité legacy BB/FK : 6 segments (dont « Panier abandonné »).
export type Segment = 'vip' | 'regular' | 'new' | 'churning' | 'lost' | 'lead';

export interface CustomerRow {
  customerId: string;
  email: string;
  name: string;
  phone: string | null;
  totalOrders: number;
  totalSpentCents: number;
  loyaltyOptIn: boolean;
  marketingConsent: boolean;
  lastActivity: string | null; // ISO date (last_stamp_at / dernière commande) ou null
  isLead?: boolean; // panier abandonné (online non payé) = contact à relancer
}

export const SEGMENT_LABELS: Record<Segment, string> = {
  vip: 'VIP',
  regular: 'Régulier',
  new: 'Nouveau',
  churning: 'À risque',
  lost: 'Perdu',
  lead: 'Panier abandonné',
};

/** Nombre de jours depuis une date ISO (ou Infinity si absente). Pur (date de réf. injectable). */
export function daysSince(iso: string | null, now: number = Date.now()): number {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Infinity;
  return (now - t) / 86_400_000;
}

/**
 * Segment d'un client (seuils portés du legacy BB/FK) :
 *  - lead     : panier abandonné (online non payé) → à relancer,
 *  - lost     : ≥ 1 commande mais aucune activité depuis > 120 jours,
 *  - churning : ≥ 1 commande mais aucune activité depuis > 45 jours (« À risque »),
 *  - vip      : ≥ 100 € dépensés OU ≥ 10 commandes,
 *  - new      : ≤ 1 commande,
 *  - regular  : sinon.
 */
export function customerSegment(
  c: Pick<CustomerRow, 'totalOrders' | 'totalSpentCents' | 'lastActivity' | 'isLead'>,
  now: number = Date.now(),
): Segment {
  if (c.isLead) return 'lead';
  const d = daysSince(c.lastActivity, now);
  if (c.totalOrders > 0 && d > 120) return 'lost';
  if (c.totalOrders > 0 && d > 45) return 'churning';
  if (c.totalSpentCents >= 10_000 || c.totalOrders >= 10) return 'vip';
  if (c.totalOrders <= 1) return 'new';
  return 'regular';
}

export interface CustomerKpis {
  count: number;
  vip: number;
  totalRevenueCents: number;
  avgBasketCents: number;
}

/** KPIs agrégés de la base clients. Pur. */
export function customerKpis(rows: CustomerRow[], now: number = Date.now()): CustomerKpis {
  const count = rows.length;
  const vip = rows.filter((r) => customerSegment(r, now) === 'vip').length;
  const totalRevenueCents = rows.reduce((s, r) => s + (r.totalSpentCents || 0), 0);
  const totalOrders = rows.reduce((s, r) => s + (r.totalOrders || 0), 0);
  const avgBasketCents = totalOrders > 0 ? Math.round(totalRevenueCents / totalOrders) : 0;
  return { count, vip, totalRevenueCents, avgBasketCents };
}

/** Filtre par segment ('all' = tous) + recherche texte (nom/email). Pur. */
export function filterCustomers(
  rows: CustomerRow[],
  opts: { segment?: Segment | 'all'; query?: string } = {},
  now: number = Date.now(),
): CustomerRow[] {
  const q = (opts.query ?? '').trim().toLowerCase();
  return rows.filter((r) => {
    if (opts.segment && opts.segment !== 'all' && customerSegment(r, now) !== opts.segment) return false;
    if (q && !(`${r.name} ${r.email}`.toLowerCase().includes(q))) return false;
    return true;
  });
}
